import styles from 'css/src/components/common/BrandKitDropdown.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import type { DropdownProps, PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { getAllBrandKits, getBrandKitById, getBrandKitColorsInHex } from 'js/src/data/brand-kits';
import type { RootState } from 'js/src/libs/RootState';

const NONE_ID = 'brand-kit-none';
const SEARCH_ID = 'brand-kit-search';

type SearchItemProps = {
    value: string;
    onChange: (value: string) => void;
};

const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
};

const BrandKitSearchItem = React.memo(function BrandKitSearchItem({
    value,
    onChange,
}: SearchItemProps) {
    return (
        <div className={styles['search-item']} onClick={stopPropagation}>
            <i translate="no" aria-hidden="true" className="material-symbols-outlined">
                search
            </i>
            <input
                type="text"
                className={styles['search-input']}
                placeholder="Search brand kits"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={stopPropagation}
                onKeyDown={stopPropagation}
                autoFocus
            />
        </div>
    );
});

type BrandKitDropdownProps = {
    brandKitIds: readonly number[];
    selectedBrandKitId: number | null;
    onSelect: (brandKitId: number | null) => void;
    placeholder?: string;
    className?: string;
    showNoneOption?: boolean;
    ToggleComponent?: DropdownProps<BrandKitSelectData>['ToggleComponent'];
    toggleProps?: Readonly<Record<string, unknown>>;
};

type ToggleProps = {
    onClick: VoidFunction;
    'aria-expanded'?: boolean | 'true' | 'false';
    ref?: React.Ref<HTMLButtonElement>;
    selectedBrandKitId: number | null;
    placeholder: string;
};

type Colors = readonly string[];

type BrandKitSelectData = { brandKitId: number | null };

type BrandKitDropdownItem = PlainDropdownItem<BrandKitSelectData> &
    Readonly<Partial<SearchItemProps>>;

const ColorDots: React.FC<{ colors: Colors }> = ({ colors }) => {
    const list = colors.slice(0, 5);
    if (list.length === 0) return null;
    return (
        <span className={styles['color-dots']}>
            {list.map((hex, i) => (
                <span key={i} className={styles['color-dot']} style={{ backgroundColor: hex }} />
            ))}
        </span>
    );
};

const WatermarkThumb: React.FC<{ brandKitId: number }> = ({ brandKitId }) => {
    const brandKit = useSelector((state: RootState) => getBrandKitById(state, brandKitId));
    const watermarkDetails = brandKit?.watermarkDetails;
    if (!watermarkDetails || watermarkDetails.length === 0) {
        return <div className={styles['watermark-thumb-placeholder']} aria-hidden="true" />;
    }
    const first = watermarkDetails[0];
    const url = first?.thumbnail_url;
    return (
        <div className={styles['watermark-thumb']}>
            <img src={url as string} alt="" />
        </div>
    );
};

const BrandKitRow: React.FC<{ brandKitId: number }> = ({ brandKitId }) => {
    const brandKit = useSelector((state: RootState) => getBrandKitById(state, brandKitId));
    const colors = useSelector((state: RootState) => getBrandKitColorsInHex(state, brandKitId));
    const name = brandKit ? brandKit.name : '';
    return (
        <>
            <WatermarkThumb brandKitId={brandKitId} />
            <span className={styles['row-text']}>
                <span className={styles['row-name']}>{name}</span>
                <ColorDots colors={colors} />
            </span>
        </>
    );
};

const BrandKitToggle = React.memo(function BrandKitToggle({
    onClick,
    'aria-expanded': ariaExpanded,
    ref,
    selectedBrandKitId,
    placeholder,
}: ToggleProps) {
    return (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-expanded={ariaExpanded}
            className={styles['brand-kit-toggle']}
            data-testid="brand-kit-dropdown-toggle"
        >
            {selectedBrandKitId != null ? (
                <BrandKitRow brandKitId={selectedBrandKitId} />
            ) : (
                <span className={styles['placeholder']}>{placeholder}</span>
            )}
            <i
                translate="no"
                aria-hidden="true"
                className={`material-symbols-outlined ${styles['caret']}`}
            >
                expand_more
            </i>
        </button>
    );
});

const BrandKitDropdown: React.FC<BrandKitDropdownProps> = ({
    brandKitIds,
    selectedBrandKitId,
    onSelect,
    placeholder = 'Select a brand kit',
    className,
    showNoneOption = true,
    ToggleComponent = BrandKitToggle,
    toggleProps: customToggleProps,
}) => {
    const [query, setQuery] = React.useState('');
    const clearQuery = React.useCallback(() => setQuery(''), []);

    const allBrandKits = useSelector(getAllBrandKits);
    const namesById = React.useMemo(() => {
        const lookup: Record<number, string> = {};
        allBrandKits.forEach((brandKit) => {
            lookup[brandKit.id] = brandKit.name;
        });
        return lookup;
    }, [allBrandKits]);

    const filteredBrandKitIds = React.useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) {
            return brandKitIds;
        }
        return brandKitIds.filter((id) => (namesById[id] ?? '').toLowerCase().includes(trimmed));
    }, [brandKitIds, namesById, query]);

    const items = React.useMemo(
        (): readonly BrandKitDropdownItem[] => [
            {
                id: SEARCH_ID,
                text: '',
                onSelectData: { brandKitId: null },
                itemComponent: BrandKitSearchItem,
                value: query,
                onChange: setQuery,
            },
            ...(showNoneOption
                ? [
                      {
                          id: NONE_ID,
                          text: 'None',
                          onSelectData: { brandKitId: null },
                          className: styles['none-item'],
                      },
                  ]
                : []),
            ...filteredBrandKitIds.map((id) => ({
                id: `brand-kit-${id}`,
                text: '',
                onSelectData: { brandKitId: id },
                className: styles['kit-item'],
                'data-testid': `brand-kit-option-${id}`,
                children: (
                    <span className={styles['item-content']}>
                        <BrandKitRow brandKitId={id} />
                    </span>
                ),
            })),
        ],
        [filteredBrandKitIds, query, showNoneOption],
    );

    const handleSelect = React.useCallback(
        (data: BrandKitSelectData) => {
            const value = data?.brandKitId ?? null;
            onSelect(value);
        },
        [onSelect],
    );

    const toggleProps = React.useMemo(
        () => ({
            selectedBrandKitId,
            placeholder,
            ...customToggleProps,
        }),
        [selectedBrandKitId, placeholder, customToggleProps],
    );

    return (
        <div className={classNames(styles['brand-kit-dropdown'], className)}>
            <Dropdown
                ToggleComponent={ToggleComponent}
                toggleProps={toggleProps}
                items={items}
                onItemSelect={handleSelect}
                afterClose={clearQuery}
                breakOverflow={true}
                limitMenuHeight={true}
            />
        </div>
    );
};

export default BrandKitDropdown;
